import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CtaSection } from "../components/CtaSection";
import { CinematicBreak } from "../components/CinematicBreak";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC, PROCESS_DETAIL } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";

const HowWeWork = () => {
  usePageMeta(
    "How We Work | Elin's Consultancy Property Support",
    "From first conversation to specialist support — the six-step journey Elin's Consultancy coordinates for every client."
  );
  const content = useContent();
  const banner = content?.banners?.how_we_work ? fileUrl(content.banners.how_we_work) : CINEMATIC.archCarved;
  return (
    <Layout>
      <PageHero
        eyebrow="A Clear and Supported Process"
        lines={[
          "From First Conversation",
          <>
            to <em className="italic font-normal text-gold-soft">Keys in Hand</em>
          </>,
        ]}
        text="Six considered steps, each coordinated by us and delivered with trusted professionals — so you always know what happens next."
        image={banner}
        imageAlt="Classical carved stonework and grand columns on a period London facade"
      />

      <section data-testid="process-detail" className="bg-cream">
        <div className="wrap-x py-20 sm:py-24 max-w-4xl mx-auto">
          {PROCESS_DETAIL.map((step, i) => (
            <Reveal key={step.num} delay={0.03}>
              <article
                data-testid={`process-step-${step.num}`}
                className={`relative grid grid-cols-[auto_1fr] gap-6 sm:gap-12 py-12 sm:py-14 ${
                  i > 0 ? "border-t border-forest/12" : ""
                }`}
              >
                <span
                  aria-hidden="true"
                  className="font-serif italic text-[72px] sm:text-[110px] leading-[0.85] text-forest/[0.09] select-none w-[80px] sm:w-[150px] text-right"
                >
                  {step.num}
                </span>
                <div>
                  <p className="eyebrow text-gold-dark">Step {step.num}</p>
                  <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest mt-3 mb-4">
                    {step.title}
                  </h2>
                  <p className="text-[14.5px] leading-[1.8] text-sage max-w-xl">{step.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CinematicBreak
        image={CINEMATIC.wideSalon}
        alt="An expansive luxury salon with double-height ceiling and bespoke seating"
        caption="The Standard We Keep"
        quote="Every detail considered. Every professional vetted. Every client kept informed."
      />

      <section data-testid="process-principles" className="bg-cream-2 border-t border-forest/10">
        <div className="wrap-x py-16 sm:py-20 grid grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto">
          {[
            { title: "One Point of Contact", text: "You speak with us; we coordinate everyone else." },
            { title: "No Tied Recommendations", text: "Professionals are introduced on merit and fit, not commission." },
            { title: "Honest Throughout", text: "We never promise investment returns or legal outcomes — only a careful process." },
          ].map((p, i) => (
            <Reveal
              key={p.title}
              delay={i * 0.12}
              className={`py-8 md:py-0 md:px-10 text-center md:text-left ${
                i > 0 ? "border-t md:border-t-0 md:border-l border-forest/15" : ""
              } ${i === 0 ? "md:pl-0" : ""}`}
            >
              <h3 className="font-serif text-2xl font-semibold text-forest mb-2.5">{p.title}</h3>
              <p className="text-[13.5px] text-sage leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaSection
        title={
          <>
            Begin With a
            <br />
            <em className="italic font-normal text-gold-soft">Conversation</em>
          </>
        }
        text="The first step is simply to talk. Tell us where you are and where you'd like to be — we'll outline the way forward."
      />
    </Layout>
  );
};

export default HowWeWork;
